import { useEffect } from 'react';
import { observer } from 'mobx-react';

import { useDependencies } from '@servicetitan/react-ioc';
import { TextField } from '@servicetitan/anvil2';

import { useFormInputs } from '../../hooks/use-form-inputs';
import { LoadStatus } from '../../../common/enums/load-status';
import { ManagePartitionStore } from '../../stores/manage-partition.store';
import { NotificationsStore } from '../../../common/stores/notifications.store';
import { ConfigurationWrapper } from '../configuration-helpers/configuration-wrapper';
import { ActiveFieldsSection } from '../configuration-helpers/active-fields-section';
import { TitleWithOpenDetails } from '../configuration-helpers/title-with-open-details';
import { OptInOutField } from '../configuration-helpers/opt-in-out-field';
import { SectionsDivider } from './sections-divider';
import { DisabledFieldsSection } from '../configuration-helpers/disabled-fields-section';

export const PartitionConfiguration = observer(() => {
    const [
        { partitionGenericConfigs, saveLoadStatus, formState, resetConfiguration, partition },
        { notifyInSnackbar },
    ] = useDependencies(ManagePartitionStore, NotificationsStore);

    const { getInputProps } = useFormInputs();

    const {
        displayName,
        emailScope,
        emailSenderDomain,
        directMailProvider,
        emailTemplateOptOut,
        authPath,
    } = formState.$;

    useEffect(() => {
        if (saveLoadStatus === LoadStatus.Ok) {
            notifyInSnackbar({ title: 'Changes saved' });
        }
    }, [saveLoadStatus, notifyInSnackbar]);

    useEffect(
        () => () => {
            resetConfiguration();
        },
        [resetConfiguration],
    );

    return (
        <ConfigurationWrapper isLoaded={!!partition}>
            <ActiveFieldsSection>
                <TitleWithOpenDetails title={partition?.displayName} />
                <TextField label="Partition Name" {...getInputProps(displayName)} />
                <TextField label="Email Sender Domain" {...getInputProps(emailSenderDomain)} />
                <TextField label="Email Scope" {...getInputProps(emailScope)} />
                <TextField label="Direct Mail Provider" {...getInputProps(directMailProvider)} />
                <TextField label="Auth Path URL" {...getInputProps(authPath)} />
                <OptInOutField field={emailTemplateOptOut} />
            </ActiveFieldsSection>
            <SectionsDivider showDivider={partitionGenericConfigs.size > 0} />
            <DisabledFieldsSection configs={partitionGenericConfigs} />
        </ConfigurationWrapper>
    );
});
